import React, { useEffect, useState, useRef } from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { marked } from "marked";
import DOMPurify from "dompurify";
import {
  View,
  Text,
  Button,
  ComboBox,
  ReadOutLoud,
  CopyToClipboard,
  Loader,
  Modal,
  ConfirmationModal,
  WistiaPlayer,
  SimpleGrid,
  ChartJS,
} from "@components";
import KeycloakAdapter from "@src/keycloak";
import { route } from "@routes/helper";
import { push } from "connected-react-router";
import * as Paths from "@routes/constants";
import {
  predefinedQuestions,
  alwaysVisibleQuestions,
  type PredefinedQuestions,
} from "./predefinedQuestions";
import { connect } from "react-redux";
import LocalStorageService from "@src/services/LocalStorageService";
import moment from "moment";
import config from "@config";
import http from "@http";
import type { AITeamSettings } from "@modules/client/reducer/service/types";
import { USER_TYPES } from "@modules/account/constants";
import { selectUserType } from "@modules/account/selectors";
import type { ChartType, ChartConfig } from "@components/chart-js";
import "./style.scss";

const COUNTRY_FLAGS = {
  EN: { name: "English", code: "en", src: "/assets/svg/flag-en.svg" },
  FR: { name: "French", code: "fr", src: "/assets/svg/flag-fr.svg" },
  DE: { name: "German", code: "de", src: "/assets/svg/flag-de.svg" },
  ES: { name: "Spanish", code: "es", src: "/assets/svg/flag-es.svg" },
  PL: { name: "Polish", code: "pl", src: "/assets/svg/flag-pl.svg" },
  BG: { name: "Bulgarian", code: "bg", src: "/assets/svg/flag-bg.svg" },
};

const morganAllowedUserTypes = [
  USER_TYPES.ADVANCED_USER,
  USER_TYPES.FULL_HR_ACCESS,
  USER_TYPES.CLIENT_ADMIN,
  USER_TYPES.SERVICE_MANAGER,
  USER_TYPES.ADMIN,
];

const agentIntroKeyById = {
  boman: "AI_TEAM_NEW_CHAT_BOMAN",
  claudia: "AI_TEAM_NEW_CHAT_CLAUDIA",
  morgan: "AI_TEAM_NEW_CHAT_MORGAN",
  shani: "AI_TEAM_NEW_CHAT_SHANI",
};

export type AgentId = "boman" | "claudia" | "morgan" | "shani";
export type Agent = {
  id: AgentId,
  name: string,
  role: string,
  disclaimer: string,
};

export type MessageUsage = {
  input_tokens: number,
  cache_creation_input_tokens: number,
  cache_read_input_tokens: number,
  cache_creation: {
    ephemeral_5m_input_tokens: number,
    ephemeral_1h_input_tokens: number,
  },
  output_tokens: number,
  service_tier: string,
  inference_geo: string,
};

export type MessageRawContent = {
  content: string[],
  model: string,
  parent_tool_use_id: string | null,
  error: string | null,
  usage: MessageUsage,
};

export type MessageRole = "user" | "assistant";
export type FeedbackValue = "up" | "down";
export type Message = {
  id: string,
  conversationId: string,
  role: MessageRole,
  content: string,
  rawContent: MessageRawContent | null,
  createdAt: string, // ISO
  feedback: FeedbackValue | null,
};
export type ConversationWithMessage = {
  ...Conversation,
  messages: Message[],
};

export type ConversationsList = Conversation[];
export type Conversation = {
  id: string,
  agent_id: AgentId,
  title: string,
  user_id: number,
  session_id: string | null,
  transcript: string | null,
  createdAt: string, // ISO
  updatedAt: string, // ISO
};

type Props = {
  toggleChat?: Function, // If not provided then its not a toggable Modal (its a Page)
  toggleAITeamChat?: Function,
  aiTeamSettings?: AITeamSettings | null,
  userType: string,
};
function AITeamChat(props: Props) {
  const aiTeamSettings = JSON.stringify(props.aiTeamSettings);
  const dontReloadConversations = useRef(false);
  const messagesWindow = useRef();
  const newChatName = useRef();
  const userInput = useRef();
  let sessionTimer = useRef(0);
  const API = config.AITeamAPI;
  const endpointBaseUrl = config.endpointBaseUrl;
  const localStorageService = LocalStorageService.getService();

  const [isRightPanel, setIsRightPanel] = useState(true);
  const [userMessage, setUserMessage] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [newConversationNameModalIsOpen: boolean, setNewConversationNameModalIsOpen] = useState(
    false,
  );

  const [agentIsWritingReply, setAgentIsWritingReply] = useState(false);
  const [partialMessage, setPartialMessage] = useState("");
  const [conversationsIsExpanded, setConversationsIsExpanded] = useState(false);
  const [agentsIsExpanded, setAgentsIsExpanded] = useState(false);
  const [agents: Agent[], setAgents] = useState(null);
  const [selectedAgent: Agent, setSelectedAgent] = useState(null);
  const [predefinedPageQuestions, setPredefinedPageQuestions] = useState([]);
  const [conversations: Conversation[], setConversations] = useState(null);
  const [selectedConversation: Conversation, setSelectedConversation] = useState(null);
  const [sessionIsExpired, setSessionIsExpired] = useState(false);
  const [agentDetailsModalToggled, setAgentDetailsModalToggled] = useState(false);
  const [selectedModalAgent, setSelectedModalAgent] = useState(null);
  const [hash, setHash] = useState(window.location.hash);

  const visibleAgents = (agents || []).filter((agent: Agent) => {
    if (agent.id === "morgan" && morganAllowedUserTypes.indexOf(props.userType) < 0) return false;
    return true;
  });

  const maxRows = 4;
  const regexp = /\n/g;
  const newLines = [...userMessage.matchAll(regexp)].length + 1;

  useEffect(() => {
    const handleHashChange = () => {
      setHash(window.location.hash);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (sessionIsExpired && sessionTimer.current) {
      clearTimeout(sessionTimer.current);
      return;
    }

    if (sessionTimer.current < 1) {
      sessionTimer.current = setTimeout(() => {
        setSessionIsExpired(true);
      }, 5 * 60 * 1000); //5min
    }

    () => {
      clearTimeout(sessionTimer.current);
      setSessionIsExpired(false);
    };
  }, [
    agentIsWritingReply,
    conversationsIsExpanded,
    agentsIsExpanded,
    agents,
    selectedAgent,
    conversations,
    selectedConversation,
    userMessage,
    isLoading,
  ]);

  useEffect(() => {
    getAgents();
  }, [aiTeamSettings]);
  useEffect(() => {
    getAgents();
  }, []);

  //Load agent from the session storage
  useEffect(() => {
    const selectedAgentID = LocalStorageService.getItem("selectedAgentID");
    if (selectedAgentID && visibleAgents.length > 0) {
      const agt = visibleAgents.find(agent => agent.id == selectedAgentID);
      if (agt) setSelectedAgent(agt);
      else LocalStorageService.removeItem("selectedAgentID");
    }
    if (agents && agents.length > 0) {
      setSelectedModalAgent("ai");
    }
  }, [agents]);

  useEffect(() => {
    if (!agents || !conversations || !selectedAgent || !selectedConversation) setIsLoading(true);
  }, [agents, conversations, selectedAgent, selectedConversation]);

  useEffect(() => {
    // Selecting default agent upon loading the list of agents
    if (selectedAgent) {
      getConversations(selectedAgent.id);
      handleSetPageQuestions(selectedAgent.id);
    }
  }, [selectedAgent]);

  useEffect(() => {
    if (hash && selectedAgent) handleSetPageQuestions(selectedAgent.id);
  }, [hash]);

  useEffect(() => {
    if (!conversations) return;
    if (dontReloadConversations.current == true) {
      dontReloadConversations.current = false;
      return;
    }
    // Load first conversation upon loading new conversations (changing the agent)
    if (conversations && conversations.length) {
      loadConversation(conversations[0].id);
    } else {
      createNewConversation(
        selectedAgent.id || "bowman",
        `New Chat ${moment().format(config.dateTimeFormat)} `,
      );
    }
  }, [conversations]);

  useEffect(() => {
    // Scroll to bottom of the chat window when selecting conversation
    if (selectedConversation) {
      scrollMessageWindowToNewestMessage();
      userInput.current?.focus();
    }
  }, [selectedConversation]);

  function scrollMessageWindowToNewestMessage() {
    setTimeout(() => {
      if (messagesWindow.current) {
        messagesWindow.current.scrollTop = messagesWindow.current.scrollHeight;
      }
    }, 100);
  }

  function getAgents() {
    http
      .get(`${API}api/ai/agents`)

      .then(({ data }) => {
        setAgents(data);
      });
  }

  function getConversations(agent_id: AgentId) {
    http
      .get(`${API}api/ai/conversations?agent_id=${agent_id}`)

      .then(({ data }) => {
        setConversations(data);
      });
  }

  function loadConversation(conversation_id: string) {
    setIsLoading(true);
    http
      .get(`${API}api/ai/conversations/${conversation_id}`)

      .then(({ data }) => {
        setSelectedConversation(data);
        setIsLoading(false);
      });
  }

  function appendMessageToTheConversation(message: Message) {
    if (!selectedConversation) return;
    const messages: Message[] = selectedConversation.messages;
    messages.push(message);
    setSelectedConversation({ ...selectedConversation, messages: messages });
  }

  function appendPartialMessage(text: string) {
    setPartialMessage(prev => {
      const next = prev + text;
      return next;
    });
  }

  function handleUserMessageChange(e) {
    setUserMessage(e.target.value);
  }

  function handleUserMessageKeyDown(e) {
    if (!e.shiftKey && e.keyCode == 13 && userMessage.trim().length > 0) {
      submitMessage();
    }
  }

  function renameConversation(data) {
    dontReloadConversations.current = true;
    const title: string = data.title;
    const conv = conversations.map(c => {
      if (c.title == selectedConversation.title) {
        return {
          ...c,
          title: title,
        };
      }
      return c;
    });

    setConversations(conv);
    setSelectedConversation({ ...selectedConversation, title: title });
  }

  async function submitMessage(message?: string) {
    setUserMessage("");
    setAgentIsWritingReply(true);

    try {
      let me = false;
      if (sessionIsExpired) {
        // Refreshing the short lived token, since for the messages we use fetch
        // We are using fetch since axios doesn't support eventStream and we
        // are going to use fetch until remove axios and re-implement @http with fetch instead of axios
        // TODO: remove that when implement fetch instead of axios
        await http.get(`${endpointBaseUrl}api/v1/profile/me`);
      }
      let BearerToken = localStorageService.getAccessToken() || KeycloakAdapter.getToken();
      const reqHeaders = new Headers();
      reqHeaders.set("Content-Type", "application/json");
      reqHeaders.set("authorization", `Bearer ${BearerToken}`);

      const res = await fetch(`${API}api/ai/conversations/${selectedConversation.id}/messages`, {
        method: "POST",
        headers: reqHeaders,
        body: JSON.stringify({ content: message || userMessage }),
      });

      if (!res.ok) {
        console.log("HTTP ERROR", res.status);
        setAgentIsWritingReply(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buf += decoder.decode(value, { stream: true });

        const lines = buf.split("\n");
        buf = lines.pop() || "";

        let evt = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) evt = line.slice(7).trim();
          else if (line.startsWith("data: ")) {
            const data = JSON.parse(line.slice(6));

            if (evt === "user_message") {
              appendMessageToTheConversation(data);
            }
            if (evt === "assistant_message") {
              setPartialMessage("");
              setAgentIsWritingReply(false);
              appendMessageToTheConversation(data);
            }
            if (evt === "partial") {
              appendPartialMessage(data.text);
            }
            if (evt === "conversation_renamed") {
              renameConversation(data);
            }
            if (evt === "done") {
              userInput.current?.focus();
            }
          }
        }
      }
    } catch (error) {
      console.log("Something went wrong", error);
      setAgentIsWritingReply(false);
      getConversations(selectedAgent.id);
    }
  }

  function handleSetPageQuestions(agentId: AgentId) {
    if (!agentId) return;
    const currentPathName = Object.keys(Paths).filter((path: string) => {
      const pat = Paths[path].replaceAll(/:[a-zA-Z]+/g, "[0-9]+").replaceAll("/", "\\/");
      const mpath =
        window.location.pathname.replaceAll(/[0-9]+/g, "[0-9]+").replaceAll("/", "\\/") + hash;
      return pat == mpath;
    })[0];

    if (currentPathName && predefinedQuestions[agentId].hasOwnProperty(currentPathName)) {
      setPredefinedPageQuestions(predefinedQuestions[agentId][currentPathName]);
    } else {
      if (predefinedPageQuestions.length) setPredefinedPageQuestions([]);
    }
  }
  function handleSubmitPredefinedQuestion(question: string) {
    submitMessage(question);
  }
  function formatMarkdownToHtml(string) {
    let rawHtml = string.replace(/^\[[A-Z]+\]/gm, "");
    rawHtml = marked(rawHtml || "", { breaks: true });
    rawHtml = replaceCountryCodesWithFlags(rawHtml);
    rawHtml = addTargetBlankToLinks(rawHtml);
    const safeHtml = DOMPurify.sanitize(rawHtml, { ADD_ATTR: ["target", "rel"] });
    return safeHtml;
  }
  function addTargetBlankToLinks(rawHtml: string): string {
    if (!rawHtml) return "";

    const container = document.createElement("div");
    container.innerHTML = rawHtml;

    const links = container.querySelectorAll("a[href]");
    for (let index = 0; index < links.length; index += 1) {
      const link = links[index];
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    }

    return container.innerHTML;
  }
  function replaceCountryCodesWithFlags(text: string) {
    return text.replace(
      /\[([A-Z]{2})\]/g,

      (_, code) => `<span class="flag ${code.toLowerCase()}"></span>`,
    );
  }

  function renderWistiaVideo(text) {
    return WistiaPlayer.extractIDFromText(text).map((wistiaVideoId, index) => (
      <WistiaPlayer key={`${wistiaVideoId}-${index}`} videoId={wistiaVideoId} />
    ));
  }

  function agentsDetailsHTML(agents: Agent[]) {
    const agentData = {
      ai: {
        id: "ai",
        name: "Goodshape AI",
        isLanding: true,
      },
      boman: {
        name: "Boman",
        jobTitle: "Customer Service Advisor",
        startDate: "25th June 2026",
        difficultyRating: "Novice",
        overview:
          "Boman focuses on helping users to make the best use of GoodShape Central. He can help you find information, teach you what things mean, assist when you're stuck, and offer advice for absence related tasks. As well as providing guidance and support, Boman can also give advice for completing return to work interviews for complex issues.",
        resources: ["GoodShape Academy", "Training Manuals", "Published Release Notes"],
        dataAndAdminAccess: {
          absenceAndEmployeeRecords: false,
          serviceConfigurationAdmin: false,
        },
        availableTo: {
          employee: false,
          manager: true,
          advancedUser: true,
          fullAccess: true,
          clientAdmin: true,
        },
      },
      claudia: {
        name: "Claudia",
        jobTitle: "Wellbeing Assistant",
        startDate: "31st July 2026",
        difficultyRating: "Novice",
        overview:
          "Claudia focuses on supporting employees that are unwell by providing safe advice and, where appropriate, signposting to support services, including those made available by the employee’s organisation. Her job is to help navigate unwell employees to the right support at the right time. She does not perform any diagnosis or replace advice from other health care professionals.",
        resources: ["Wellbeing Advice Library", "Services Hub", "Clinical Governance Policies"],
        dataAndAdminAccess: {
          absenceAndEmployeeRecords: false,
          serviceConfigurationAdmin: false,
        },
        availableTo: {
          employee: true,
          manager: true,
          advancedUser: true,
          fullAccess: true,
          clientAdmin: true,
        },
      },
      morgan: {
        name: "Morgan",
        jobTitle: "Data Scientist",
        startDate: "30th September 2026",
        difficultyRating: "Advanced",
        overview:
          "Morgan focuses on data analysis and is available to support senior managers and HR teams with interrogating absence data and identifying issues hot spots and areas to focus on. He can be used to analyse historic absence trends, predict future absence rates, and create charts and tables for presentations.",
        resources: ["Absence Data Records", "Employee Data Records", "Management Data Records"],
        dataAndAdminAccess: {
          absenceAndEmployeeRecords: true,
          serviceConfigurationAdmin: false,
        },
        availableTo: {
          employee: false,
          manager: false,
          advancedUser: true,
          fullAccess: true,
          clientAdmin: true,
        },
      },
      shani: {
        name: "Shani",
        jobTitle: "Policy Advisor",
        startDate: "16th December 2026",
        difficultyRating: "Expert",
        overview:
          "Shani focuses on supporting HR teams and can advise on policy changes and assist with the configuration and administration of the platform. With access to your entire service set-up, Shani can help enable, disable, update and improve items such as alerts, return to work interview forms and much more.",
        resources: [
          "Service Configuration",
          "Best Practice Indicators",
          "Absence and Wellbeing Policies",
        ],
        dataAndAdminAccess: {
          absenceAndEmployeeRecords: false,
          serviceConfigurationAdmin: true,
        },
        availableTo: {
          employee: false,
          manager: false,
          advancedUser: false,
          fullAccess: false,
          clientAdmin: true,
        },
      },
    };
    const booleanColumns = [
      { name: "label", renderItem: row => row.label },
      {
        name: "value",
        align: "center",
        width: 50,
        renderItem: row => (
          <span
            className={`material-icons agent-details-bool ${row.value ? "positive" : "negative"}`}
          >
            {row.value ? "check_circle" : "cancel"}
          </span>
        ),
      },
    ];

    function renderLanding() {
      const adviceRows = [1, 2, 3, 4, 5].map(i => ({
        id: i,
        advice: props.t(`AI_TEAM_LANDING_ADVICE_${i}`),
      }));

      const difficultyRows = ["NOVICE", "ADVANCED", "EXPERT"].map(level => ({
        id: level,
        rating: props.t(`AI_TEAM_DIFFICULTY_${level}`),
        description: props.t(`AI_TEAM_DIFFICULTY_${level}_TEXT`),
      }));

      const difficultyColumns = [
        {
          name: "rating",
          width: 100,
          align: "center",
          renderItem: row => (
            <span className={`difficulty-rating ${row.rating.toLowerCase()}`}>{row.rating}</span>
          ),
        },
        { name: "description", renderItem: row => row.description },
      ];

      return (
        <View className="agent-details-container" direction="row" flex="1" gap="4">
          <View flex="1" direction="column" gap="3">
            <View direction="column" gap="1">
              <Text type="h6">{props.t("AI_TEAM_LANDING_INTRODUCTION_TITLE")}</Text>
              <Text text={props.t("AI_TEAM_LANDING_INTRODUCTION_TEXT")} />
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("AI_TEAM_LANDING_TRAINING_TITLE")}</Text>
              <Text text={props.t("AI_TEAM_LANDING_TRAINING_TEXT")} />
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("AI_TEAM_LANDING_DATA_RETENTION_TITLE")}</Text>
              <Text text={props.t("AI_TEAM_LANDING_DATA_RETENTION_TEXT_1")} />
              <Text text={props.t("AI_TEAM_LANDING_DATA_RETENTION_TEXT_2")} />
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("AI_TEAM_LANDING_DIFFICULTY_TITLE")}</Text>
              <Text text={props.t("AI_TEAM_LANDING_DIFFICULTY_TEXT")} />
              <SimpleGrid
                className="theme-help"
                rows={difficultyRows}
                columns={difficultyColumns}
                noResultsText=""
              />
            </View>
          </View>

          <View flex="1" direction="column" gap="3">
            <View direction="column" gap="1">
              <Text type="h6">{props.t("AI_TEAM_LANDING_AVAILABILITY_TITLE")}</Text>
              <Text
                text={props.t("AI_AGENTS_ONLY_APPEAR_IF_THEY_ARE_ENABLED_BY_YOUR_ORGANISATION")}
              />
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("AI_TEAM_LANDING_MISTAKES_TITLE")}</Text>
              <Text text={props.t("AI_TEAM_LANDING_MISTAKES_TEXT")} />
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("AI_TEAM_LANDING_CONFIDENTIALITY_TITLE")}</Text>
              <Text text={props.t("AI_TEAM_LANDING_CONFIDENTIALITY_TEXT")} />
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("AI_TEAM_LANDING_ADVICE_TITLE")}</Text>
              <SimpleGrid
                className="theme-help"
                rows={adviceRows}
                columns={[{ name: "advice", renderItem: row => row.advice }]}
                noResultsText=""
              />
            </View>
          </View>
        </View>
      );
    }

    function renderAgentDetails(agent) {
      const accessRows = [
        {
          id: "absenceAndEmployeeRecords",
          label: "Absence & Employee Records",
          value: agent.dataAndAdminAccess.absenceAndEmployeeRecords,
        },
        {
          id: "serviceConfigurationAdmin",
          label: "Service Configuration Admin",
          value: agent.dataAndAdminAccess.serviceConfigurationAdmin,
        },
      ];

      const availabilityRows = [
        { id: "employee", label: "Employee", value: agent.availableTo.employee },
        { id: "manager", label: "Manager", value: agent.availableTo.manager },
        { id: "advancedUser", label: "Advanced User", value: agent.availableTo.advancedUser },
        { id: "fullAccess", label: "Full Access", value: agent.availableTo.fullAccess },
        { id: "clientAdmin", label: "Client Admin", value: agent.availableTo.clientAdmin },
      ];

      return (
        <View className="agent-details-container" direction="row" flex="1" gap="4">
          <View flex="1" direction="column" gap="3">
            <View direction="column" gap="1">
              <Text>
                <strong>{props.t("Name")}</strong>: {agent.name}
              </Text>
              <Text>
                <strong>{props.t("Job Title")}</strong>: {agent.jobTitle}
              </Text>
              <Text>
                <strong>{props.t("Start Date")}</strong>: {agent.startDate}
              </Text>
              <Text>
                <strong>{props.t("DIFFICULTY_RATING")}</strong>:{" "}
                <span className={`difficulty-rating ${agent.difficultyRating.toLowerCase()}`}>
                  {props.t(`AI_TEAM_DIFFICULTY_${agent.difficultyRating.toUpperCase()}`)}
                </span>
              </Text>
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("Overview")}</Text>
              <Text text={agent.overview} />
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("Resources:")}</Text>
              <SimpleGrid
                className="theme-help"
                rows={agent.resources.map((r, i) => ({ id: i, resource: r }))}
                columns={[{ name: "resource", renderItem: row => row.resource }]}
                noResultsText=""
              />
            </View>
          </View>

          <View flex="1" direction="column" gap="3">
            <View direction="column" gap="1">
              <Text type="h6">{props.t("Data & Admin Access:")}</Text>
              <SimpleGrid
                className="theme-help"
                rows={accessRows}
                columns={booleanColumns}
                noResultsText=""
              />
            </View>
            <View direction="column" gap="1">
              <Text type="h6">{props.t("Available To:")}</Text>
              <SimpleGrid
                className="theme-help"
                rows={availabilityRows}
                columns={booleanColumns}
                noResultsText=""
              />
            </View>
          </View>
        </View>
      );
    }

    function renderModalBody() {
      const agent = agentData[selectedModalAgent] || agentData.ai;
      if (agent.isLanding) return renderLanding();
      return renderAgentDetails(agent);
    }

    return (
      <View className="agent-details-modal" gap="4">
        <View className="agent-details-modal" direction="column" gap="4">
          <View className="agent-details-selector" gap="2" direction="row">
            {[agentData.ai, ...agents].map((agent: Agent) => (
              <View
                key={agent.id}
                justifyContent="center"
                className={`agent-details-avatar ${agent.id}${
                  selectedModalAgent === agent.id ? " selected" : ""
                }`}
                onClick={() => setSelectedModalAgent(agent.id)}
              >
                {agent.id === "ai" && <Text text={props.t("AI")} />}
              </View>
            ))}
          </View>
          {renderModalBody()}
        </View>
      </View>
    );
  }

  function updateMessageFeedback(messageId: string, feedback: FeedbackValue | null) {
    const messages = selectedConversation.messages.map(m =>
      m.id === messageId ? { ...m, feedback } : m,
    );
    setSelectedConversation({ ...selectedConversation, messages });
  }

  function handleVote(message: Message, value: FeedbackValue) {
    return function() {
      const isDeleting = message.feedback === value;
      const method = isDeleting ? "delete" : "post";
      http[method](
        `${API}api/ai/conversations/${selectedConversation.id}/messages/${message.id}/feedback`,
        isDeleting ? undefined : { feedback: value },
      ).then(() => updateMessageFeedback(message.id, isDeleting ? null : value));
    };
  }

  // Extracts ```chart ... ``` blocks from the message text so they can be
  // rendered as real ChartJS components instead of HTML/canvas injected via innerHTML.
  function extractCharts(text: string): { charts: ChartConfig[], strippedText: string } {
    if (!text) return { charts: [], strippedText: text || "" };

    const charts = [];
    const strippedText = text.replace(/```chart\s*\n([\s\S]*?)```/g, (_, jsonString) => {
      try {
        const config = JSON.parse(jsonString.trim());
        const chartTypes = ["bar", "bubble", "pie", "doughnut", "line"];
        if (config && chartTypes.includes(config.type) && config.data) {
          charts.push(config);
        }
      } catch (e) {
        console.log("Failed to parse chart block JSON", e);
      }
      return "";
    });

    return { charts, strippedText };
  }

  function renderCharts(charts: ChartConfig[]) {
    return charts.map((chart, index) => (
      <View key={`chart-${index}`} className="ai-chart-wrapper" gap="2">
        <ChartJS title={chart.title} type={chart.type} data={chart.data} options={chart.options} />
      </View>
    ));
  }

  function generateConversation(conversation: ?ConversationWithMessage) {
    return (
      conversation &&
      conversation.messages.map((message: Message) => {
        const feedback = message.feedback;
        let disabled = null;
        if (feedback == "up") disabled = "down";
        if (feedback == "down") disabled = "up";
        const { charts, strippedText } = extractCharts(message.content);
        return (
          <View
            key={message.id}
            className={`${message.role} ${selectedAgent.id} message`}
            direction="column"
            gap="3"
          >
            <View className="content" html={formatMarkdownToHtml(strippedText)} />
            {renderCharts(charts)}
            {renderWistiaVideo(message.content)}
            <View direction="row">
              <View flex="1" alignItems={message.role === "assistant" ? "flex-start" : "flex-end"}>
                <View direction="row" gap="4">
                  <Text text={moment(message.createdAt).format(config.dateTimeFormatShortYear)} />
                  {message.role == "assistant" && (
                    <View className="vote-container" direction="row" gap="2">
                      <div
                        onClick={handleVote(message, "up")}
                        size="1.3"
                        className={`thumb-up trigger ${feedback === "up" &&
                          "selected"} ${disabled && disabled == "up" && "disabled"}`}
                      ></div>
                      <div
                        onClick={handleVote(message, "down")}
                        size="1.3"
                        className={`thumb-down trigger ${feedback === "down" &&
                          "selected"} ${disabled && disabled == "down" && "disabled"}`}
                      ></div>
                    </View>
                  )}
                </View>
              </View>
              {message.role == "assistant" && (
                <View flex="1" alignItems="flex-end">
                  <View direction="row" gap="3">
                    <View className="data-tooltip" data-tooltip="Listen the message">
                      <ReadOutLoud
                        text={message.content}
                        gender={
                          selectedAgent.id == "boman" || selectedAgent.id == "morgan"
                            ? "male"
                            : "female"
                        }
                      />
                    </View>
                    <View className="data-tooltip" data-tooltip="Copy to clipboard">
                      <CopyToClipboard text={message.content} />
                    </View>
                  </View>
                </View>
              )}
            </View>
          </View>
        );
      })
    );
  }
  function renderNewChatIntro() {
    return (
      <View className="new-chat-intro" alignItems="center" gap="4">
        <View className={`agent-avatar ${selectedAgent.id}`} />
        <Text type="h4" text={props.t("AI_TEAM_NEW_CHAT_GREETING")} />
        <Text text={props.t(agentIntroKeyById[selectedAgent.id])} />
      </View>
    );
  }

  function handleToggleChat() {
    if (!selectedAgent && visibleAgents.length > 0) {
      const agent = visibleAgents[0];
      handleSelectAgent(agent.id)();
      props.toggleChat(true);
    } else {
      setSelectedAgent(null);
      // Save / Load agent to the session storage
      LocalStorageService.removeItem("selectedAgentID");
      props.toggleChat(false);
    }
  }

  function handleSelectAgent(agentId: AgentId) {
    return function() {
      setIsLoading(true);
      const agent = visibleAgents.find((agent: Agent) => agent.id == agentId);
      setSelectedAgent(agent);
      // Save / Load agent to the session storage
      LocalStorageService.setItem("selectedAgentID", agent.id);
      props.toggleChat(true);
    };
  }

  function handleSelectConversation(uniqueId: string) {
    const con = conversations.find(c => `${c.id}|${c.title}` === uniqueId);
    loadConversation(con.id);
  }

  function handleExitChat() {
    if (props.toggleAITeamChat) {
      props.toggleAITeamChat();
    } else {
      props.push(route("HOME_PATH"));
    }
  }

  function handleSwitchView() {
    setIsRightPanel(!isRightPanel);
  }

  function handleToggleNewConversationName() {
    setNewConversationNameModalIsOpen(!newConversationNameModalIsOpen);
  }

  function handleCreateNewConversation() {
    handleToggleNewConversationName();
    const newConversationName = newChatName.current.value;
    createNewConversation(selectedAgent.id, newConversationName);
  }

  function createNewConversation(agentId, conversationName) {
    return http
      .post(`${API}api/ai/conversations`, {
        agent_id: agentId,
        title: conversationName,
      })

      .then(({ data }) => {
        getConversations(selectedAgent.id);
      });
  }

  function handleCreateNewConversationKeyDown(e) {
    if (e.keyCode === 13) {
      handleCreateNewConversation();
    }
  }

  function handleDeleteConversation(conversation_id) {
    return function(e) {
      e.stopPropagation();
      deleteConversation(conversation_id);
    };
  }

  function handleSubmitMessageButton() {
    submitMessage();
  }

  function deleteConversation(conversation_id: string) {
    http
      .delete(`${API}api/ai/conversations/${conversation_id}`, {
        method: "DELETE",
      })
      .then(({ data }) => {
        getConversations(selectedAgent.id);
      });
  }

  function toggleAgentDetailsModal(conversation_id: string) {
    setAgentDetailsModalToggled(!agentDetailsModalToggled);
  }
  function exportTableToExcel(table) {
    const html = `<html>
      <head>
        <meta charset="UTF-8">
        <style>
          body{
            font-family: "Roboto", "Arial", "Helvetica Neue", sans-serif;
          }
          table {
            margin-bottom: 1rem;
            margin-top: 1rem;
            width: 100%;
          }
          table tr {
            font-weight: 700;
            background-color: #ffddb4;
            border-bottom: 2px solid #fff !important;
          }
          
          table th {
            padding: 2px 4px;
            text-align: left;
          }
          table th:first-child {
            border-right: 2px solid #fff !important;
          }
          table td {
            padding: 2px 4px;
            font-size: 12px;
            color: #000;
            border-bottom: 2px solid #fff;
            background-color: #f5f5f5;
          }
          table td:first-child {
            background-color: #d9dcde;
            border-right: 2px solid #fff !important;
          }
        </style>
      </head>
      <body>
        ${table}
      </body>
    </html>`;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "table-export.xls";
    a.click();
    URL.revokeObjectURL(url);
  }

  const questionsToShow = [
    ...predefinedPageQuestions,
    ...((selectedAgent && alwaysVisibleQuestions[selectedAgent.id]) || []),
  ];

  return (
    <View className="ai-team-chat" direction="row">
      {/* COLLAPSED */}
      <View className="agent-select-sidebar">
        <View className="header" direction="row">
          {<Button onClick={handleToggleChat} materialIcon="menu" />}
        </View>
        <Button
          className="ai-sidebar-text"
          color="black"
          label={props.t("AI")}
          onClick={toggleAgentDetailsModal}
        />
        {visibleAgents.map((agent: Agent) => {
          return (
            <View key={agent.id}>
              <Button
                title={`${agent.name}: ${agent.role}`}
                data-title={agent.name}
                className={selectedAgent && selectedAgent.id === agent.id && "selected"}
                onClick={handleSelectAgent(agent.id)}
              />
            </View>
          );
        })}

        {visibleAgents.length > 0 && agentDetailsModalToggled && (
          <ConfirmationModal
            headerText={props.t("Goodshape AI")}
            bodyContent={agentsDetailsHTML(visibleAgents)}
            cancelBtnLabel={props.t("Close")}
            modalSize="large"
            onCancel={toggleAgentDetailsModal}
          />
        )}
      </View>
      {/* COLLAPSED END */}
      {/* EXPANDED */}
      {selectedAgent && (
        <View className="messages-content" position="relative" flex="1" overflow="auto" gap="2">
          {isLoading && <Loader inline />}
          <View className="header" direction="row" gap="5" justifyContent="space-between">
            <View alignItems="center" direction="row" gap={2}>
              <View className={`selected-agent ${selectedAgent.id} `} />
              <View>
                <Text type="h5" text={selectedAgent.name} />
                <Text text={selectedAgent.role} />
              </View>
            </View>
            <View direction="row" gap="2" flex="1" justifyContent="flex-end">
              {/* NEW CONVERSATIONS */}
              <Button
                className="data-tooltip"
                data-tooltip="New Chat"
                disabled={agentIsWritingReply}
                onClick={handleToggleNewConversationName}
                materialIcon="edit"
              />
              {newConversationNameModalIsOpen && (
                <ConfirmationModal
                  headerText="Chat name..."
                  bodyContent={
                    <View flex="1">
                      <input
                        ref={newChatName}
                        onKeyDown={handleCreateNewConversationKeyDown}
                        defaultValue={`New Chat ${moment().format(config.dateTimeFormat)} `}
                      />
                    </View>
                  }
                  confirmBtnLabel="Continue"
                  cancelBtnLabel="Cancel"
                  onConfirm={handleCreateNewConversation}
                  onCancel={handleToggleNewConversationName}
                  modalSize="small"
                />
              )}
              {conversations && selectedConversation && (
                <View
                  className="data-tooltip"
                  flex="1"
                  data-tooltip="Search previous chats..."
                  maxWidth={80}
                >
                  <ComboBox
                    classNamePrefix={` ai-team conversation-selector`}
                    disabled={agentIsWritingReply}
                    onChange={handleSelectConversation}
                    value={`${selectedConversation.id}|${selectedConversation.title}`}
                    options={conversations.map((conv: Conversation) => {
                      return {
                        value: `${conv.id}|${conv.title}`,
                        label: (
                          <View direction="row">
                            <View flex="1">
                              <Text className="title">{conv.title}</Text>
                            </View>
                            <View width="25px" height="25px" alignSelf="center">
                              <Button
                                onClick={handleDeleteConversation(conv.id)}
                                fillSpace
                                color="negative"
                                size="small"
                                materialIcon="close"
                              />
                            </View>
                          </View>
                        ),
                      };
                    })}
                  />
                </View>
              )}
            </View>
          </View>

          {/* MESSAGES */}
          <View ref={messagesWindow} className="conversation" flex="1">
            {selectedConversation?.messages?.length < 1 &&
              !agentIsWritingReply &&
              renderNewChatIntro()}
            {generateConversation(selectedConversation)}
            {/* AGEENT REPLY ANIMATION */}
            {agentIsWritingReply && (
              <View className={`assistant ${selectedAgent.id} message`}>
                <View className="content">
                  {renderCharts(extractCharts(partialMessage).charts)}
                  {renderWistiaVideo(partialMessage)}
                  <div className="typing" aria-label="Assistant is typing">
                    <div className="typing__dot"></div>
                    <div className="typing__dot"></div>
                    <div className="typing__dot"></div>
                  </div>
                </View>
              </View>
            )}
          </View>

          {/* USER MESSAGE + SEND BUTTON */}
          <View className="send-message" gap="2">
            {/* PREDEFINED MESSAGES */}

            {questionsToShow.length > 0 &&
              selectedConversation?.messages?.length < 1 &&
              !agentIsWritingReply && (
                <View alignItems="end" gap="2">
                  {questionsToShow.map((question, index) => (
                    <Button
                      key={`${question}-${index}`}
                      disabled={agentIsWritingReply}
                      label={question}
                      onClick={() => handleSubmitPredefinedQuestion(question)}
                    />
                  ))}
                </View>
              )}
            {/* DISCLAIMER */}

            <View direction="row" gap="2">
              <View flex="1">
                <textarea
                  placeholder="Type your message here..."
                  style={{ minHeight: "3.1rem" }}
                  ref={userInput}
                  disabled={agentIsWritingReply}
                  onChange={handleUserMessageChange}
                  onKeyDown={handleUserMessageKeyDown}
                  rows={newLines > maxRows ? maxRows : newLines}
                  value={userMessage}
                ></textarea>
              </View>
              <View>
                {/* SUBMIT MESSAGE */}
                <Button
                  disabled={agentIsWritingReply || userMessage.trim().length < 1}
                  onClick={handleSubmitMessageButton}
                  fillSpace
                  materialIcon="arrow_upward"
                />
              </View>
            </View>
            <Text size={0.7} className="message-box" text={selectedAgent.disclaimer} />
          </View>
        </View>
        /* EXPANDED END */
      )}
    </View>
  );
}

const mapStateToProps = state => ({
  userType: selectUserType(state),
});

export default compose(connect(mapStateToProps, { push }), localize())(AITeamChat);
